/**
 * Builders compartidos para targetPattern de misiones de ritmo.
 */
import type {
  AttackFinger,
  RhythmSubMission,
  RhythmTargetNote,
} from "./rhythmPathPlan";

export const att = (
  pitch: string,
  duration: number,
  finger: AttackFinger
): RhythmTargetNote => ({ type: "attack", pitch, duration, finger });

export const rst = (duration: number): RhythmTargetNote => ({
  type: "rest",
  duration,
});

export function repeatNotes(
  cycle: RhythmTargetNote[],
  times: number
): RhythmTargetNote[] {
  const out: RhythmTargetNote[] = [];
  for (let t = 0; t < times; t++) out.push(...cycle);
  return out;
}

export function zipFingerPitch(
  items: Array<{ pitch: string; finger: AttackFinger }>,
  duration: number
): RhythmTargetNote[] {
  return items.map((it) => att(it.pitch, duration, it.finger));
}

/** Ciclo de pitches con el mismo dedo (p. ej. solo pulgar). */
export function cyclePitchesWithFinger(
  cycle: string[],
  count: number,
  duration: number,
  finger: AttackFinger
): RhythmTargetNote[] {
  return Array.from({ length: count }, (_, i) =>
    att(cycle[i % cycle.length], duration, finger)
  );
}

export function alternateIM(
  n: number,
  start: "i" | "m" = "i"
): AttackFinger[] {
  const out: AttackFinger[] = [];
  let f: AttackFinger = start;
  for (let i = 0; i < n; i++) {
    out.push(f);
    f = f === "i" ? "m" : "i";
  }
  return out;
}

const TOL = 150;
const ENDURANCE_RATIO = 0.9;

export function makeStep(
  missionTag: string,
  partial: Omit<RhythmSubMission, "targetPattern"> & {
    targetPattern: RhythmTargetNote[];
    instructions: [string, string, string];
    tip: string;
    minAttacks?: number;
  }
): RhythmSubMission {
  const attacks = partial.targetPattern.filter((n) => n.type === "attack");
  const min = partial.minAttacks ?? 4;
  if (attacks.length < min) {
    throw new Error(
      `[${missionTag}] "${partial.id}" tiene ${attacks.length} ataques; mínimo ${min}.`
    );
  }
  const { minAttacks: _m, ...rest } = partial;
  return {
    ...rest,
    attackFingers: attacks.map((n) => n.finger!).filter(Boolean),
    toleranceMs: partial.toleranceMs ?? TOL,
    bpm: partial.bpm ?? 60,
    successRatio:
      partial.successRatio ??
      (partial.isEndurance || partial.kind === "resistencia"
        ? ENDURANCE_RATIO
        : undefined),
  };
}
